import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent 
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import NavBar from '../components/common/NavBar';
import StripePaymentForm from '../components/payments/StripePaymentForm';
import DevPaymentForm from '../components/payments/DevPaymentForm';
import api from '../services/api';
import { createCheckoutSubscription } from '../services/billingService';
import { REACT_APP_STRIPE_PUBLISHABLE_KEY, STRIPE_CONFIG } from '../config/Config';

// Load Stripe (will use test key in development)
const stripePromise = loadStripe(REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);

  useEffect(() => {
    // Prorated upgrade: only confirm existing PaymentIntent (no plan selection)
    if (location.state?.isUpgradePayment && location.state?.clientSecret) {
      setPlanDetails({
        isUpgradePayment: true,
        clientSecret: location.state.clientSecret,
        title: location.state.planName || 'Upgrade'
      });
      setLoading(false);
      return;
    }

    // Get plan details from location state or session storage
    const planCode = location.state?.planCode || searchParams.get('planCode') || sessionStorage.getItem('planCode');
    const planTitle = location.state?.planTitle || searchParams.get('planTitle') || sessionStorage.getItem('planTitle');
    const planPrice = location.state?.planPrice || searchParams.get('planPrice') || sessionStorage.getItem('planPrice');
    const planPeriod = location.state?.planPeriod || searchParams.get('planPeriod') || sessionStorage.getItem('planPeriod');
    const changeReason = location.state?.changeReason || '';
    
    // Get referral code from location state or session storage
    const referralCode = location.state?.referralCode || searchParams.get('referralCode') || sessionStorage.getItem('referralCode');
    
    // Get user email from location state or session storage
    const userEmail = location.state?.email || searchParams.get('email') || sessionStorage.getItem('userEmail');

    if (!planCode) {
      enqueueSnackbar('No plan selected. Please choose a plan first.', { variant: 'error' });
      navigate('/pricing');
      return;
    }

    setPlanDetails({
      code: planCode,
      title: planTitle,
      price: planPrice,
      period: planPeriod,
      changeReason: changeReason,
      referralCode: referralCode,
      userEmail: userEmail
    });

    if (location.state?.fromProfile) {
      fetchStripeConfig();
      // Prefer Stripe Checkout redirect when backend returns a URL (no secret logic in frontend)
      createCheckoutSubscription(planCode, '/profile', '/profile')
        .then((data) => {
          if (data?.url) {
            window.location.href = data.url;
            return;
          }
        })
        .catch(() => {});
    } else {
      fetchStripeConfig();
    }
  }, [location, navigate, enqueueSnackbar, searchParams]);

  const fetchStripeConfig = async () => {
    try {
      const response = await api.get(STRIPE_CONFIG);
      setStripeConfig(response.data);
    } catch (err) {
      console.error('Error fetching Stripe config:', err);
      // Don't show error for unauthenticated users (coming from registration)
      // Just set loading to false so they can see the development payment form
      if (err.response?.status !== 401) {
        enqueueSnackbar('Failed to load payment configuration', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (subscriptionData) => {
    if (location.state?.isUpgradePayment) {
      enqueueSnackbar('Upgrade complete. Your new plan is now active.', { variant: 'success' });
      navigate('/profile', { replace: true, state: { message: 'Upgrade complete.' } });
      return;
    }
    enqueueSnackbar('Payment successful! Your subscription is now active.', { variant: 'success' });
    
    // Check if this payment came from profile page
    if (location.state?.fromProfile) {
      navigate('/profile', {
        replace: true,
        state: { message: 'Subscription updated successfully!' }
      });
    } else {
      navigate('/login', {
        state: {
          message: 'Registration and payment completed successfully! Please log in.',
          email: location.state?.email
        }
      });
    }
  };

  const handlePaymentCancel = () => {
    enqueueSnackbar('Payment cancelled. You can complete payment later.', { variant: 'info' });
    
    // Check if this payment came from profile page
    if (location.state?.fromProfile) {
      // Redirect back to profile page, replacing the current history entry
      navigate('/profile', { replace: true });
    } else {
      // Redirect to login (registration flow)
      navigate('/login', {
        state: {
          message: 'Registration completed. Please log in to complete payment.',
          email: location.state?.email
        }
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!planDetails) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography>No plan selected</Typography>
        </Box>
      </Box>
    );
  }

  // Upgrade payment: only confirm PaymentIntent (webhook applies access)
  if (planDetails.isUpgradePayment) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
        <NavBar />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <Card sx={{ maxWidth: 600, width: '100%', mx: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom align="center">
                Complete your upgrade
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Complete payment below. Your new plan will be active as soon as payment is confirmed.
              </Alert>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body1">
                  Plan: {planDetails.title}
                </Typography>
              </Box>
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  clientSecretProp={planDetails.clientSecret}
                  confirmOnly
                  planName={planDetails.title}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </Elements>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  // Default to development mode if Stripe config is not available (e.g., unauthenticated users)
  const isDevelopment = stripeConfig ? !stripeConfig.isProduction : false;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <NavBar />
      
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <Card sx={{ maxWidth: 600, width: '100%', mx: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
              Complete Your Subscription
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              {isDevelopment 
                ? 'Development Mode: This is a test payment. No real charges will be made.'
                : 'Please complete your payment to activate your subscription.'
              }
            </Alert>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Plan Details
              </Typography>
              <Typography variant="body1">
                Plan: {planDetails.title}
              </Typography>
              <Typography variant="body1">
                Price: {planDetails.price}{planDetails.period}
              </Typography>
            </Box>

            {isDevelopment ? (
              <DevPaymentForm
                planCode={planDetails.code}
                planName={planDetails.title}
                planPrice={planDetails.price}
                changeReason={planDetails.changeReason}
                referralCode={planDetails.referralCode}
                userEmail={planDetails.userEmail}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            ) : (
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  planCode={planDetails.code}
                  planName={planDetails.title}
                  planPrice={planDetails.price}
                  changeReason={planDetails.changeReason}
                  referralCode={planDetails.referralCode}
                  userEmail={planDetails.userEmail}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </Elements>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PaymentPage; 
